import { handleHttpRequest, http, type HttpResponse } from 'interfaces'
import { type BaseAddress, revertTransformAddress, transformAddress, type UsPhone } from 'services'

export interface ExecOrgPhone extends UsPhone {
  execOrgId: number
}

export interface ExecOrgAddress extends BaseAddress {
  execOrgId: number
  phones: ExecOrgPhone[]
}

// The API serializes the address in the flat P_/S_ shape; map it back to the
// nested ExecOrgAddress used across the UI.
const toExecOrgAddress = (data: any): ExecOrgAddress => ({
  id: data.id,
  execOrgId: data.execOrgId,
  typeId: 1,
  primary: transformAddress(data, 'primary'),
  secondary: transformAddress(data, 'secondary'),
  mailingAddress: data.mailingAddress,
  phones: Array.isArray(data.phones) ? data.phones : [],
  displayOrder: data.displayOrder,
  updatedBy: data.updatedBy,
  lastUpdated: data.lastUpdated
})

export async function getExecOrgAddress (orgId: number): Promise<ExecOrgAddress | null> {
  return await http.get(`/api/v1/us/executives/orgs/${orgId}/address/`).then(response => {
    const data = response.data
    return data != null && typeof data === 'object' && !Array.isArray(data) ? toExecOrgAddress(data) : null
  })
}

// Map the nested ExecOrgAddress back to the flat P_/S_ API shape. Shared by the
// org-update PATCH and the org-create payload (TI-4053), so the create path
// reuses this transform rather than re-deriving it.
export const execOrgAddressToApi = (payload: ExecOrgAddress, orgId: number): Record<string, unknown> => ({
  id: payload.id,
  execOrgId: orgId,
  ...revertTransformAddress(payload.primary, 'primary'),
  ...revertTransformAddress(payload.secondary, 'secondary'),
  mailingAddress: payload.mailingAddress,
  displayOrder: payload.displayOrder,
  updatedBy: payload.updatedBy,
  phones: payload.phones.map(phone => ({
    ...phone,
    id: phone.id != null && phone.id < 0 ? 0 : phone.id
  })),
  lastUpdated: payload.lastUpdated
})

export async function updateExecOrgAddress (orgId: number, addressId: number, payload: ExecOrgAddress): Promise<HttpResponse<ExecOrgAddress>> {
  const response = await handleHttpRequest<any>(
    http.patch(`/api/v1/us/executives/orgs/${orgId}/address/${addressId}/`, execOrgAddressToApi(payload, orgId))
  )

  // The PATCH returns the persisted address in the flat P_/S_ shape; map it back
  // to the nested ExecOrgAddress so response.result matches the declared type.
  return {
    ...response,
    result: response.result != null ? toExecOrgAddress(response.result) : null
  }
}
